import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperience,
  IExperienceTemplate,
  MessageStatus
} from '@dadohr/shared/domainTypes/experience';
import { PreviewItemType } from '@dadohr/shared/domainTypes/experiencePreview';
import { ExperienceReplacements } from '../../../../services/set/interpolation';
import { CF } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { useMemo, useState } from 'react';
import {
  FilterByActorChip,
  FilterByItemTypeChip
} from '../../../../components/Filters';
import { TableSearch } from '../../../../components/Table';
import { useUnsafeTranslation } from '../../../../i18next';
import { Centered } from '../../../../layout/Centered';
import { FlexContainer } from '../../../../layout/Flex';
import {
  forceRunAutomationActions,
  groupConcreteMessages,
  moveAutomationToToday,
  moveMessageToToday,
  moveTaskToToday,
  openSubTask,
  reopenAutomation,
  skipAutomation,
  skipAutomationAction,
  skipMessage,
  skipSubTask,
  skipSubTaskAndTrigger,
  skipTask,
  unskipAutomation,
  unskipAutomationAction,
  unskipMessage,
  unskipTask
} from '../../../../services/experiences/instance';
import { updateDoc } from '../../../../services/firebase';
import { callFirebaseFunction } from '../../../../services/firebaseFunctions';
import { ItemData } from '../../../../services/participatDialog/participantDialogHelpers';
import { toSearchTermRegexp } from '../../../../services/user';
import { useActorDescriptorsFromTemplate } from '../../components/Actors';
import { getItemDisplayName } from '../../helpers/adminName';
import { getModifications } from './modifications';
import { Automation } from './ParticipantDialogComponents/Automation';
import { Message } from './ParticipantDialogComponents/Message';
import { Task } from './ParticipantDialogComponents/Task';

export type Selections = {
  tasks: { [taskTplId: string]: boolean };
  messages: { [msgTplId: string]: boolean };
  automations: { [aTplId: string]: boolean };
};

const TaskAndAutomationList = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridRowGap: p.theme.spacing()
}));

const StickyFilterContainer = styled(FlexContainer)((p) => ({
  position: 'sticky',
  background: p.theme.palette.background.paper,
  zIndex: 1,

  // This is all in relation to other paddings within the component this is used in,
  // specifically a dialog
  marginTop: p.theme.spacing(-3),
  paddingTop: p.theme.spacing(2),
  top: p.theme.spacing(-2),
  marginBottom: p.theme.spacing(2)
}));

export const TimelineItemStatusList = ({
  tpl,
  originalD,
  d,
  setD,
  selections,
  setSelections,
  timelineItems,
  replacements,
  onRestartDocusignSubTask
}: {
  tpl: Doc<IExperienceTemplate>;
  originalD: Doc<IExperience>;
  d: Doc<IExperience>;
  setD: (value: React.SetStateAction<Doc<IExperience>>) => void;
  selections: Selections;
  setSelections: (value: React.SetStateAction<Selections>) => void;
  timelineItems: ItemData[];
  replacements?: ExperienceReplacements;
  onRestartDocusignSubTask: (
    trigger: {
      taskTplId: string;
      subTaskTplId: string;
      resourceId: string;
    },
    items: {
      taskTplId: string;
      subTaskTplId: string;
      resourceId: string;
    }[],
    voidedReason: string | null
  ) => Promise<void>;
}) => {
  const { t } = useUnsafeTranslation();
  const actorDescriptors = useActorDescriptorsFromTemplate(
    tpl.data.supportingActors
  );

  const groupedConcreteMessages = useMemo(
    () => groupConcreteMessages(d.data.messages),
    [d.data.messages]
  );

  const [filters, setFilters] = useState({
    q: '',
    itemTypes: [
      PreviewItemType.MESSAGE,
      PreviewItemType.TASK,
      PreviewItemType.AUTOMATION
    ],
    actorKeys: [...Object.keys(actorDescriptors), 'AUTOMATION']
  });

  const modifications = useMemo(() => getModifications(originalD, d), [
    originalD,
    d
  ]);

  const isMessageModified = (id: string) =>
    !!modifications.messages[id]?.changed;

  const isAutomationActionModified = (automationId: string, actionId: string) =>
    !!modifications.automations[automationId]?.actions[actionId]?.changed;

  const isAutomationModified = (automationId: string) =>
    !!modifications.automations[automationId]?.changed;

  const isTaskModified = (taskId: string) =>
    !!modifications.tasks[taskId]?.changed;

  const isSubTaskModified = (taskId: string, subTaskId: string): boolean =>
    !!modifications.tasks[taskId]?.subTasks[subTaskId]?.changed;

  const filteredTimelineItems = useMemo(() => {
    let xs = timelineItems;

    if (filters.q) {
      const re = toSearchTermRegexp(filters.q);
      xs = xs.filter((x) => {
        const displayName = getItemDisplayName(x.data);
        const title =
          x.type === 'TASK'
            ? x.data.title
            : x.type === 'MESSAGE'
            ? x.data.subject
            : x.type === 'AUTOMATION'
            ? x.data.title
            : '';
        return displayName.match(re) || title.match(re);
      });
    }
    xs = xs.filter((x) =>
      filters.itemTypes.includes(x.type as PreviewItemType)
    );
    xs = xs.filter((x) => {
      if (x.type === 'AUTOMATION') return filters.actorKeys.includes('AUTOMATION');
      if (filters.actorKeys.includes(x.actor.key)) return true;
      return x.ccActorKeys.some((k) => filters.actorKeys.includes(k));
    });
    return xs;
  }, [timelineItems, filters]);

  const isCCView = (item: ItemData) =>
    item.type !== 'AUTOMATION' &&
    !filters.actorKeys.includes(item.actor.key) &&
    item.ccActorKeys.some((k) => filters.actorKeys.includes(k));

  return (
    <>
      <StickyFilterContainer justifyContent="flex-end">
        <FilterByActorChip
          value={filters.actorKeys}
          onChange={(actorKeys) => setFilters((x) => ({ ...x, actorKeys }))}
          actorDescriptors={actorDescriptors}
        />
        <FilterByItemTypeChip
          value={filters.itemTypes}
          onChange={(itemTypes) => setFilters((x) => ({ ...x, itemTypes }))}
        />
        <TableSearch
          term={filters.q}
          onChange={(q) => setFilters((x) => ({ ...x, q }))}
          placeholder={t('Search by title')}
        />
      </StickyFilterContainer>
      <TaskAndAutomationList>
        {!filteredTimelineItems.length && (
          <Centered height={300}>
            <Typography variant="caption">
              {t('No timeline items found.')}
            </Typography>
          </Centered>
        )}
        {filteredTimelineItems.map((item) => (
          <>
            {item.type === 'TASK' && (
              <Task
                key={`task-${item.data.id}`}
                tpl={tpl}
                task={item.data}
                actor={item.actor}
                isCCView={isCCView(item)}
                audience={item.audience}
                channels={item.channels}
                orgId={d.data.orgId}
                replacements={replacements}
                customizedTiming={item.customizedTiming}
                selected={selections.tasks[item.data.templateId]}
                onSelect={(nextSelected) =>
                  setSelections((x) => ({
                    ...x,
                    tasks: {
                      ...x.tasks,
                      [item.data.templateId]: nextSelected
                    }
                  }))
                }
                onSkipTask={() => setD(skipTask(tpl, d, item.data.id))}
                onSkipSubTask={(subTaskId) => {
                  setD(skipSubTask(tpl, d, item.data.id, subTaskId))
                }}
                onSkipLastSubTask={async (subTaskId: string) => {
                  await skipSubTaskAndTrigger(
                    tpl, 
                    originalD,  // Original state from DB
                    d,          // Current state with local changes
                    item.data.id, 
                    subTaskId
                  ); // backend updates the doc and view should update automatically
                }}
                onUnSkipTask={() => setD(unskipTask(tpl, d, item.data.id))}
                onOpenSubTask={(subTaskId) =>
                  setD(openSubTask(tpl, d, item.data.id, subTaskId))
                }
                template={tpl.data}
                onForceTrigger={() => 
                  callFirebaseFunction(CF.experiences.forceTriggerItem, {
                    orgId: d.data.orgId,
                    tplId: tpl.id,
                    instanceId: d.id,
                    itemType: item.type,
                    itemTemplateId: item.data.templateId
                  })
                }
                isModified={isTaskModified(item.data.id)}
                isSubtaskModified={isSubTaskModified}
                onSendToday={() => setD(moveTaskToToday(tpl, d, item.data.id))}
                triggeredBy={item.triggeredBy}
                onRestartDocusignSubTask={onRestartDocusignSubTask}
                concreteMessagesForTask={
                  groupedConcreteMessages.byTask[item.data.id]
                }
              />
            )}
            {item.type === 'AUTOMATION' && (
              <Automation
                key={`aut-${item.data.id}`}
                a={item.data}
                audience={item.audience}
                customizedTiming={item.customizedTiming}
                onReopenAutomation={() =>
                  setD(reopenAutomation(d, item.data.templateId))
                }
                onForceRunAutomationAction={(actionTplId) =>
                  forceRunAutomationActions(
                    tpl.data.orgId,
                    [
                      {
                        experienceTplId: tpl.id,
                        automationTplId: item.data.templateId,
                        actionTplId,
                        instanceId: d.id
                      }
                    ],
                    Date.now()
                  )
                }
                onForceRunAutomationActions={(actionTplIds) =>
                  forceRunAutomationActions(
                    tpl.data.orgId,
                    actionTplIds.map((actionTplId) => ({
                      experienceTplId: tpl.id,
                      automationTplId: item.data.templateId,
                      actionTplId,
                      instanceId: d.id
                    })),
                    Date.now()
                  )
                }
                onSkipAutomation={() =>
                  setD(skipAutomation(d, item.data.templateId))
                }
                onUnskipAutomation={() =>
                  setD(unskipAutomation(d, item.data.templateId))
                }
                onSkipAction={(acTplId) =>
                  setD(skipAutomationAction(d, item.data.templateId, acTplId))
                }
                onUnskipAction={(acTplId) =>
                  setD(unskipAutomationAction(d, item.data.templateId, acTplId))
                }
                checkIfActionModified={(aId: string, acId: string) =>
                  isAutomationActionModified(aId, acId)
                }
                template={tpl.data}
                onForceTrigger={() => 
                  callFirebaseFunction(CF.experiences.forceTriggerItem, {
                    orgId: d.data.orgId,
                    tplId: tpl.id,
                    instanceId: d.id,
                    itemType: item.type,
                    itemTemplateId: item.data.templateId
                  })
                }
                selected={selections.automations[item.data.templateId]}
                onSelect={(nextSelected) =>
                  setSelections((x) => ({
                    ...x,
                    automations: {
                      ...x.automations,
                      [item.data.templateId]: nextSelected
                    }
                  }))
                }
                onRunToday={() =>
                  setD(moveAutomationToToday(tpl, d, item.data.templateId))
                }
                isModified={isAutomationModified(item.data.templateId)}
                supportingActors={tpl.data.supportingActors}
                triggeredBy={item.triggeredBy}
              />
            )}
            {item.type === 'MESSAGE' && (
              <Message
                key={`msg-${item.data.templateId}`}
                msgId={item.data.templateId}
                msg={item.data}
                actor={item.actor}
                isCCView={isCCView(item)}
                audience={item.audience}
                channels={item.channels}
                experienceId={d.id}
                orgId={d.data.orgId}
                replacements={replacements}
                customizedTiming={item.customizedTiming}
                selected={selections.messages[item.data.templateId]}
                onSelect={(nextSelected) =>
                  setSelections((x) => ({
                    ...x,
                    messages: {
                      ...x.messages,
                      [item.data.templateId]: nextSelected
                    }
                  }))
                }
                onSuccessfulResend={async () => {
                  d.data.messagesStatuses[item.data.templateId].status =
                    MessageStatus.DONE;
                  await updateDoc(d, (x) => ({
                    messagesStatuses: x.messagesStatuses
                  }));
                }}
                onSkipMessage={() => setD(skipMessage(d, item.data.templateId))}
                onUnskipMessage={() =>
                  setD(unskipMessage(d, item.data.templateId))
                }
                onSendToday={() =>
                  setD(moveMessageToToday(tpl, d, item.data.templateId))
                }
                template={tpl.data}
                onForceTrigger={() => 
                  callFirebaseFunction(CF.experiences.forceTriggerItem, {
                    orgId: d.data.orgId,
                    tplId: tpl.id,
                    instanceId: d.id,
                    itemType: 'MSG', // item.type, here the type seems to be saved as 'MESSAGE' instead of 'MSG'
                    itemTemplateId: item.data.templateId
                  })
                }
                concreteMessages={
                  groupedConcreteMessages.byMessage[item.data.templateId] || []
                }
                isModified={isMessageModified(item.data.templateId)}
                triggeredBy={item.triggeredBy}
              />
            )}
          </>
        ))}
      </TaskAndAutomationList>
    </>
  );
};
